import React, { useState, useEffect } from 'react';
import { API_URL } from '../backend_url';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Snackbar,
  Chip,
  Link,
  Button,
  Grid,
  Card,
  CardContent,
  Divider,
  Container,
  CardHeader,
  useTheme,
  alpha,
  Stack,
  LinearProgress
} from '@mui/material';
import {
  OpenInNew as OpenInNewIcon,
  ContentCopy as ContentCopyIcon,
  Assignment as AssignmentIcon,
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
  Dashboard as DashboardIcon,
  TrendingUp as TrendingUpIcon,
  EmojiEvents as TrophyIcon,
  Description as DescriptionIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Star as StarIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../firebase';
import { useNavigate } from 'react-router-dom';

const UserSubmissionsPage = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [user] = useAuthState(auth);
  const [stats, setStats] = useState({
    totalSubmissions: 0,
    averageScore: 0,
    bestScore: 0
  });
  const navigate = useNavigate();
  const theme = useTheme();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const token = await user.getIdToken();
        
        // Fetch submissions and stats
        const [submissionsRes, statsRes] = await Promise.all([
          axios.get('https://harshanpvtserver.duckdns.org/form-pulse/my-submissions', {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get('https://harshanpvtserver.duckdns.org/form-pulse/my-submissions/stats', {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);

        // Process submissions with form settings
        const processedSubmissions = await Promise.all(
          submissionsRes.data.map(async (sub) => {
            try {
              const formRes = await axios.get(`https://harshanpvtserver.duckdns.org/form-pulse/form/${sub.form_name}`, {
                headers: { Authorization: `Bearer ${token}` }
              });
              return {
                ...sub,
                total_possible_marks: sub.total_possible_marks || 1,
                show_answers: formRes.data.show_answers !== false,
                is_protected: formRes.data.protected || false,
                form_title: formRes.data.title || sub.form_name
              };
            } catch (err) {
              console.error(`Error fetching form ${sub.form_name}:`, err);
              return {
                ...sub,
                total_possible_marks: sub.total_possible_marks || 1,
                show_answers: true,
                is_protected: false,
                form_title: sub.form_name
              };
            }
          })
        );

        setSubmissions(processedSubmissions);
        setStats(statsRes.data);
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to load submissions');
      } finally {
        setLoading(false);
      }
    };

    if (user) fetchData();
  }, [user]);

  const handleCopyLink = async (formName) => {
    try {
      const formLink = `${window.location.origin}/form/${encodeURIComponent(formName)}`;
      await navigator.clipboard.writeText(formLink);
      setSnackbarOpen(true);
    } catch (err) {
      setError('Failed to copy link to clipboard');
    }
  };

  const handleRefresh = () => {
    setLoading(true);
    setError('');
    setSubmissions([]);
    // Retrigger the useEffect
    const token = user.getIdToken();
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const calculatePercentage = (marks, total) => {
    return total > 0 ? ((marks / total) * 100).toFixed(1) : 0;
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, textAlign: 'center' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            minHeight: '50vh',
            justifyContent: 'center'
          }}
        >
          <CircularProgress size={60} />
          <Typography variant="h6" color="text.secondary">
            Loading your submissions...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert 
          severity="error" 
          sx={{ maxWidth: 600, mx: 'auto', borderRadius: 2 }}
          action={
            <Button 
              color="inherit" 
              size="small"
              onClick={handleRefresh}
              sx={{ borderRadius: 2 }}
            >
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header Section */}
      <Box sx={{ mb: 6 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <DashboardIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Typography 
            variant="h3" 
            component="h1" 
            sx={{ 
              fontWeight: 'bold',
              background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              color: 'transparent'
            }}
          >
            My Submissions
          </Typography>
        </Box>
        <Typography variant="h6" color="text.secondary">
          Track your form submissions and performance
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card 
            elevation={8} 
            sx={{ 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              transition: 'transform 0.3s ease-in-out',
              '&:hover': { transform: 'translateY(-5px)' }
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h6" sx={{ opacity: 0.9 }}>
                    Total Submissions
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                    {stats.totalSubmissions}
                  </Typography>
                </Box>
                <DescriptionIcon sx={{ fontSize: 48, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card 
            elevation={8} 
            sx={{ 
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              color: 'white',
              transition: 'transform 0.3s ease-in-out',
              '&:hover': { transform: 'translateY(-5px)' }
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h6" sx={{ opacity: 0.9 }}>
                    Average Score
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                    {stats.averageScore || 0}%
                  </Typography>
                </Box>
                <TrendingUpIcon sx={{ fontSize: 48, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card 
            elevation={8} 
            sx={{ 
              background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
              color: 'white',
              transition: 'transform 0.3s ease-in-out',
              '&:hover': { transform: 'translateY(-5px)' }
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h6" sx={{ opacity: 0.9 }}>
                    Best Score
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                    {stats.bestScore || 0}%
                  </Typography>
                </Box>
                <TrophyIcon sx={{ fontSize: 48, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {submissions.length === 0 ? (
        <Card elevation={8} sx={{ textAlign: 'center', py: 8 }}>
          <CardContent>
            <AssignmentIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
              No Submissions Yet
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 400, mx: 'auto' }}>
              You haven't submitted any forms yet. Start exploring and submitting forms to track your progress here.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/')}
              sx={{ 
                px: 4,
                py: 1.5,
                borderRadius: 2,
                background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #5a67d8 30%, #6b46c1 90%)',
                }
              }}
            >
              Browse Forms
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card elevation={8}>
          <CardHeader
            title="Your Submissions"
            subheader="Review your form submissions and performance"
            sx={{ 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              '& .MuiCardHeader-subheader': { color: 'rgba(255,255,255,0.8)' }
            }}
            avatar={<AssignmentIcon sx={{ color: 'white' }} />}
          />
          <CardContent sx={{ p: 0 }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow 
                    sx={{ 
                      background: alpha(theme.palette.primary.main, 0.1),
                    }}
                  >
                    <TableCell sx={{ fontWeight: 'bold' }}>Form Details</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Submitted</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Score</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Performance</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {submissions.map((submission, index) => {
                    const percentage = calculatePercentage(submission.total_marks, submission.total_possible_marks);
                    return (
                      <TableRow 
                        key={index} 
                        hover
                        sx={{ 
                          '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.05) }
                        }}
                      >
                        <TableCell>
                          <Box>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                              {submission.form_title}
                            </Typography>
                            <Stack direction="row" spacing={1}>
                              {submission.is_protected && (
                                <Chip 
                                  label="Proctored" 
                                  size="small" 
                                  color="warning"
                                  icon={<StarIcon />}
                                />
                              )}
                              <Chip 
                                label={submission.show_answers ? "Graded" : "Submitted"}
                                size="small"
                                color={submission.show_answers ? "success" : "info"}
                                icon={submission.show_answers ? <CheckCircleIcon /> : <ScheduleIcon />}
                              />
                            </Stack>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={submission.show_answers ? "Results Available" : "Under Review"} 
                            color={submission.show_answers ? "success" : "info"} 
                            variant="outlined"
                            sx={{ fontWeight: 'bold' }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {new Date(submission.submitted_at).toLocaleDateString()}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(submission.submitted_at).toLocaleTimeString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {submission.show_answers ? (
                            <Box>
                              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                {submission.total_marks} / {submission.total_possible_marks}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                points
                              </Typography>
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              Hidden
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {submission.show_answers ? (
                            <Box>
                              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                                {percentage}%
                              </Typography>
                              <LinearProgress 
                                variant="determinate" 
                                value={Number(percentage)} 
                                sx={{ 
                                  height: 6,
                                  borderRadius: 1,
                                  '& .MuiLinearProgress-bar': {
                                    backgroundColor: percentage >= 80 ? 'success.main' : 
                                                   percentage >= 60 ? 'warning.main' : 'error.main'
                                  }
                                }}
                              />
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              Hidden
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={1} justifyContent="center">
                            <Tooltip title="View Form">
                              <IconButton
                                component={Link}
                                href={`${window.location.origin}/form/${encodeURIComponent(submission.form_name)}`}
                                target="_blank"
                                rel="noopener"
                                color="primary"
                                sx={{ 
                                  '&:hover': { 
                                    backgroundColor: alpha(theme.palette.primary.main, 0.1) 
                                  }
                                }}
                              >
                                <OpenInNewIcon />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="Copy Form Link">
                              <IconButton
                                onClick={() => handleCopyLink(submission.form_name)}
                                color="primary"
                                sx={{ 
                                  '&:hover': { 
                                    backgroundColor: alpha(theme.palette.primary.main, 0.1) 
                                  }
                                }}
                              >
                                <ContentCopyIcon />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity="success"
          sx={{ width: '100%', borderRadius: 2 }}
          variant="filled"
        >
          Form link copied to clipboard!
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default UserSubmissionsPage;